// BMC Coach support: tickets + chat, from the Coach portal.
//
// Same tables and RPCs as the creator app's support chat
// (creator-app/services/support.ts). A ticket is a row in `user_feedback`, its
// thread lives in `support_conversations` / `support_messages`, and every write
// goes through a SECURITY DEFINER support_* RPC.
//
// What makes it a COACH ticket: `user_feedback.is_coach = true`, set once by
// support_open_ticket(p_is_coach => true). See migration
// 20260930000003_support_coach_portal_tickets.sql. The requester is still a
// creator, so `source` stays 'creator'. Every read here filters on is_coach, so
// the portal lists coach tickets, plus app tickets an admin merged with one of
// them (user_feedback.cross_portal, migration 20261027000001). The mobile app
// lists everything the user owns and labels the Coach-only ones. bmc-admin
// works them in its BMC Coach tab.
import { supabase } from '@/lib/supabase';
import { BRAND_WORKER_URL } from '@/services/roster';

export const MAX_SUPPORT_MESSAGE_LEN = 4000;
/** The new-request form's cap. Matches the creator app's Help screen. */
export const MAX_TICKET_MESSAGE_LEN = 1000;
export const MAX_SUBJECT_LEN = 160;
/** Mirrors the creator app's cap. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/** Matches the DB-side cap in support_post_message(). */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;
const ATTACH_BUCKET = 'support-attachments';
const ATTACH_SIGNED_TTL = 60 * 60 * 24 * 7; // 7 days

export type TicketType = 'bug_report' | 'feature_request' | 'support' | 'general';
export type TicketStatus = 'open' | 'in_progress' | 'waiting_on_customer' | 'follow_up_needed' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type SupportSenderType = 'customer' | 'agent' | 'system';
/** BMC Assistant lifecycle — see creator-app migration 20261002000002_support_ai_assistant.sql. */
export type TicketHandledBy = 'human' | 'ai';
export type TicketAiStatus = 'active' | 'resolved' | 'handed_off' | null;

export interface SupportTicket {
  ticket_id: string;
  ticket_number: number | null;
  type: TicketType;
  subject: string | null;
  message: string;
  status: TicketStatus;
  priority: TicketPriority;
  created_at: string | null;
  last_activity_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  handled_by: TicketHandledBy;
  ai_status: TicketAiStatus;
  /** Support merged this ticket into another one. The list hides it; opening
   *  it redirects to this ticket instead. */
  merged_into_ticket_id: string | null;
  conversation_id: string | null;
  conversation_status: 'active' | 'closed' | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender_type: SupportSenderType | null;
  message_count: number;
}

export interface ReadState {
  last_read_at: string | null;
}

export interface SupportAttachment {
  url: string;
  path?: string;
  name: string;
  mime: string;
  size: number;
  kind: 'image' | 'file';
  width?: number;
  height?: number;
}

export interface SupportMessage {
  id: string;
  conversation_id: string;
  sender_type: SupportSenderType;
  sender_user_id: string | null;
  sender_name: string | null;
  body: string;
  attachments: SupportAttachment[];
  created_at: string;
  edited_at: string | null;
  /** Written by BMC Assistant (always sender_type 'agent'). */
  is_ai: boolean;
  /** Set by BMC Assistant when it points at a specific in-app page — see
   *  COACH_SUPPORT_LINKS in TicketThread.tsx for the fixed key → route map. */
  link_key: string | null;
}

/** What the customer is told an agent is called. The row's real sender_name is
 *  the agent's email, which the customer must never see. */
export const SUPPORT_AGENT_LABEL = 'BMC Coach Support';
/** What an AI-written reply (is_ai) is labelled as instead. */
export const SUPPORT_AI_LABEL = 'BMC Assistant';

/** True while BMC Assistant is answering this ticket. */
export function isAiHandling(t: Pick<SupportTicket, 'handled_by' | 'ai_status' | 'status'>): boolean {
  return t.handled_by === 'ai' && t.ai_status === 'active' && t.status !== 'closed';
}

/** A sender can fix their own message for this long after sending it. The RPC
 *  (support_edit_message) enforces the same window. */
export const SUPPORT_MESSAGE_EDIT_WINDOW_MS = 5 * 60 * 1000;

export function canEditMessage(m: Pick<SupportMessage, 'sender_type' | 'sender_user_id' | 'created_at'>, userId: string | null | undefined): boolean {
  if (!userId || m.sender_type === 'system' || m.sender_user_id !== userId) return false;
  return Date.now() - new Date(m.created_at).getTime() <= SUPPORT_MESSAGE_EDIT_WINDOW_MS;
}

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_on_customer: 'Waiting for your reply',
  follow_up_needed: 'Still waiting on your reply',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  bug_report: 'Something’s broken',
  feature_request: 'Idea for Coach',
  support: 'Help',
  general: 'General',
};

export const ACTIVE_TICKET_STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting_on_customer', 'follow_up_needed'];
export const DONE_TICKET_STATUSES: TicketStatus[] = ['resolved', 'closed'];
export type TicketListScope = 'active' | 'done';

/** A resolved ticket stays writable on purpose: replying is how a customer
 *  reopens it. Only closed is final. */
export function canCustomerReply(status: TicketStatus): boolean {
  return status !== 'closed';
}

/** A ticket's main title: the opening line of the creator's first message.
 *  Not the subject. The new-request form seeds the subject from a topic
 *  ("Connecting Claude"), so several tickets can share one, while the first
 *  message is what actually tells them apart. Falls back to the subject, then
 *  the type. */
export function ticketHeadline(t: Pick<SupportTicket, 'subject' | 'message' | 'type'>): string {
  const firstLine = (t.message || '').split('\n').find((l) => l.trim().length > 0)?.trim();
  if (firstLine) return firstLine.length > 90 ? `${firstLine.slice(0, 90)}…` : firstLine;
  return t.subject?.trim() || TICKET_TYPE_LABELS[t.type] || 'Support request';
}

/** The secondary label shown next to the headline: the subject, or the type
 *  when a ticket has no subject. */
export function ticketSubjectLabel(t: Pick<SupportTicket, 'subject' | 'type'>): string {
  return t.subject?.trim() || TICKET_TYPE_LABELS[t.type] || 'Support';
}

export type SupportAttachmentKind = 'image' | 'video' | 'pdf' | 'file';

export function attachmentKind(a: Pick<SupportAttachment, 'mime' | 'kind' | 'name'>): SupportAttachmentKind {
  const mime = (a.mime || '').toLowerCase();
  const name = (a.name || '').toLowerCase();
  if (mime.startsWith('image/') || a.kind === 'image') return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return 'file';
}

export function attachmentDownloadUrl(a: Pick<SupportAttachment, 'url' | 'name'>): string {
  if (!a.url) return a.url;
  const sep = a.url.includes('?') ? '&' : '?';
  return `${a.url}${sep}download=${encodeURIComponent(a.name || 'file')}`;
}

/** Saves an attachment to disk. Fetches it and saves the blob so the browser
 *  downloads it instead of navigating to it. If the fetch fails (network, CORS),
 *  falls back to the storage URL with its `download` parameter, which Supabase
 *  serves as an attachment, so the browser still downloads it. */
export async function downloadAttachment(a: Pick<SupportAttachment, 'url' | 'name'>): Promise<void> {
  if (!a?.url) throw new Error('This file is not available to download.');
  try {
    const res = await fetch(a.url);
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const objUrl = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = objUrl;
    link.download = a.name || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
  } catch {
    window.open(attachmentDownloadUrl(a), '_blank', 'noopener');
  }
}

export function fileSizeLabel(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Attachments ─────────────────────────────────────────────────────────────

/** The bucket is private and stored URLs are signed for 7 days, so they go
 *  dead. Re-sign from the storage path on every fetch. */
function attachmentStoragePath(a: SupportAttachment): string | null {
  if (a.path) return a.path;
  const m = a.url?.match(/\/object\/sign\/support-attachments\/([^?]+)/);
  return m ? m[1] : null;
}

async function resignAttachmentUrls(messages: SupportMessage[]): Promise<SupportMessage[]> {
  const paths = new Set<string>();
  for (const m of messages) {
    for (const a of m.attachments ?? []) {
      const p = attachmentStoragePath(a);
      if (p) paths.add(p);
    }
  }
  if (paths.size === 0) return messages;
  const { data, error } = await supabase.storage.from(ATTACH_BUCKET).createSignedUrls([...paths], ATTACH_SIGNED_TTL);
  if (error || !data) return messages;
  const urlByPath = new Map<string, string>();
  for (const d of data) if (d.path && d.signedUrl) urlByPath.set(d.path, d.signedUrl);
  if (urlByPath.size === 0) return messages;
  return messages.map((m) => (!m.attachments?.length ? m : {
    ...m,
    attachments: m.attachments.map((a) => {
      const p = attachmentStoragePath(a);
      const url = p ? urlByPath.get(p) : undefined;
      return url ? { ...a, url } : a;
    }),
  }));
}

function imageSize(file: File): Promise<{ width?: number; height?: number }> {
  if (!file.type.startsWith('image/')) return Promise.resolve({});
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve({}); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

/** Returns an error message for a file that can't be attached, or null. */
export function validateAttachment(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return `${file.name} is over 25 MB.`;
  if (file.size === 0) return `${file.name} is empty.`;
  return null;
}

/** Uploads into the conversation's folder. The storage RLS policy checks
 *  folder[1] against the caller's own support_conversations row. */
export async function uploadSupportAttachment(conversationId: string, file: File): Promise<SupportAttachment> {
  const mime = file.type || 'application/octet-stream';
  const kind: SupportAttachment['kind'] = mime.startsWith('image/') ? 'image' : 'file';
  const path = `${conversationId}/${Date.now()}-${file.name.replace(/[^\w.-]/g, '_')}`;
  const { error } = await supabase.storage.from(ATTACH_BUCKET).upload(path, file, { contentType: mime });
  if (error) throw error;
  const [{ data }, dims] = await Promise.all([
    supabase.storage.from(ATTACH_BUCKET).createSignedUrl(path, ATTACH_SIGNED_TTL),
    imageSize(file),
  ]);
  return { url: data?.signedUrl ?? '', path, name: file.name, mime, size: file.size, kind, ...dims };
}

// ── Reads ───────────────────────────────────────────────────────────────────

const TICKET_COLUMNS =
  'id, ticket_number, type, subject, message, status, priority, created_at, last_activity_at, resolved_at, closed_at, handled_by, ai_status, merged_into_ticket_id';
const CONV_COLUMNS =
  'id, ticket_id, status, last_message_at, last_message_preview, last_message_sender_type, message_count';

interface TicketDbRow {
  id: string;
  ticket_number: number | null;
  type: TicketType;
  subject: string | null;
  message: string;
  status: TicketStatus;
  priority: TicketPriority;
  created_at: string | null;
  last_activity_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  handled_by: TicketHandledBy;
  ai_status: TicketAiStatus;
  merged_into_ticket_id: string | null;
}

interface ConvDbRow {
  id: string;
  ticket_id: string;
  status: 'active' | 'closed';
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender_type: SupportSenderType | null;
  message_count: number;
}

function mergeTicket(t: TicketDbRow, c: ConvDbRow | undefined): SupportTicket {
  return {
    ticket_id: t.id,
    ticket_number: t.ticket_number,
    type: t.type,
    subject: t.subject,
    message: t.message,
    status: t.status,
    priority: t.priority,
    created_at: t.created_at,
    last_activity_at: t.last_activity_at,
    resolved_at: t.resolved_at,
    closed_at: t.closed_at,
    handled_by: t.handled_by ?? 'human',
    ai_status: t.ai_status ?? null,
    merged_into_ticket_id: t.merged_into_ticket_id ?? null,
    conversation_id: c?.id ?? null,
    conversation_status: c?.status ?? null,
    last_message_at: c?.last_message_at ?? null,
    last_message_preview: c?.last_message_preview ?? null,
    last_message_sender_type: c?.last_message_sender_type ?? null,
    message_count: c?.message_count ?? 0,
  };
}

async function attachConversations(tickets: TicketDbRow[]): Promise<SupportTicket[]> {
  if (tickets.length === 0) return [];
  const { data: convos, error } = await supabase
    .from('support_conversations')
    .select(CONV_COLUMNS)
    .in('ticket_id', tickets.map((t) => t.id));
  if (error) throw error;
  const byTicket = new Map(((convos ?? []) as ConvDbRow[]).map((c) => [c.ticket_id, c]));
  return tickets.map((t) => mergeTicket(t, byTicket.get(t.id)));
}

/** Every coach ticket this user has, newest activity first. RLS scopes it to
 *  the caller; is_coach / cross_portal scope it to the portal. A coach customer has a
 *  handful of tickets, so this is unpaginated, unlike the mobile list. */
export async function listMyCoachTickets(): Promise<SupportTicket[]> {
  const { data, error } = await supabase
    .from('user_feedback')
    .select(TICKET_COLUMNS)
    // Its own tickets, plus app tickets an admin merged with one of them
    // (cross_portal, same user): those live in both places.
    .or('is_coach.eq.true,cross_portal.eq.true')
    // Support merged this ticket into another one; only the primary is shown.
    .is('merged_into_ticket_id', null)
    .order('last_activity_at', { ascending: false, nullsFirst: false })
    .limit(200);
  if (error) throw error;
  return attachConversations((data ?? []) as TicketDbRow[]);
}

/** One coach ticket. Returns null for a mobile-app ticket id too, so the
 *  portal never opens a thread that belongs only to the app. A merged
 *  (cross_portal) ticket belongs to both, so it opens here as well. */
export async function getCoachTicket(ticketId: string): Promise<SupportTicket | null> {
  const { data, error } = await supabase
    .from('user_feedback')
    .select(TICKET_COLUMNS)
    .eq('id', ticketId)
    .or('is_coach.eq.true,cross_portal.eq.true')
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const [merged] = await attachConversations([data as TicketDbRow]);
  return merged ?? null;
}

/** Thread messages, oldest first. RLS hides internal notes from the customer. */
export async function getMessages(conversationId: string): Promise<SupportMessage[]> {
  const { data, error } = await supabase
    .from('support_messages')
    .select('id, conversation_id, sender_type, sender_user_id, sender_name, body, attachments, created_at, edited_at, is_ai, link_key')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return resignAttachmentUrls((data ?? []) as SupportMessage[]);
}

export async function fetchReadStates(userId: string): Promise<Record<string, ReadState>> {
  const { data, error } = await supabase
    .from('support_conversation_reads')
    .select('conversation_id, last_read_at')
    .eq('user_id', userId);
  if (error) throw error;
  const map: Record<string, ReadState> = {};
  for (const r of (data ?? []) as { conversation_id: string; last_read_at: string | null }[]) {
    map[r.conversation_id] = { last_read_at: r.last_read_at };
  }
  return map;
}

/** This viewer's read cursor for one thread, as it is right now. Call it
 *  BEFORE the thread is marked read: opening a conversation advances the
 *  cursor, and the "New messages" line needs the value from before that. */
export async function fetchReadCursor(conversationId: string, userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('support_conversation_reads')
    .select('last_read_at')
    .eq('conversation_id', conversationId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as { last_read_at: string | null } | null)?.last_read_at ?? null;
}

/** The message the "New messages" line goes above: the first one from support
 *  (or a status update) newer than the cursor, using the same "not mine" rule
 *  as isTicketUnread. Null when nothing is unread, or when the unread run
 *  starts at the very top of the thread, where a line would separate nothing. */
export function firstUnreadMessageId(messages: SupportMessage[], lastReadAt: string | null): string | null {
  const cursor = lastReadAt ? new Date(lastReadAt).getTime() : null;
  const idx = messages.findIndex((m) => m.sender_type !== 'customer'
    && (cursor === null || new Date(m.created_at).getTime() > cursor));
  return idx > 0 ? messages[idx].id : null;
}

/** Support (or a status change) said something the customer hasn't seen. */
export function isTicketUnread(
  t: Pick<SupportTicket, 'conversation_id' | 'last_message_at' | 'last_message_sender_type'>,
  states: Record<string, ReadState>,
): boolean {
  if (!t.conversation_id || !t.last_message_at || t.last_message_sender_type === 'customer') return false;
  const st = states[t.conversation_id];
  return !st?.last_read_at || st.last_read_at < t.last_message_at;
}

// ── Writes ──────────────────────────────────────────────────────────────────

export interface OpenedTicket {
  ticketId: string;
  conversationId: string;
  ticketNumber: number | null;
}

/** Creates the coach ticket, its thread and the first message in one
 *  transaction. p_is_coach is what makes it a coach ticket. */
export async function openCoachTicket(params: { type: TicketType; message: string; subject?: string | null }): Promise<OpenedTicket> {
  const { data, error } = await supabase.rpc('support_open_ticket', {
    p_type: params.type,
    p_message: params.message.trim(),
    p_subject: params.subject?.trim() || null,
    p_source: 'creator',
    p_is_coach: true,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.ticket_id || !row?.conversation_id) {
    throw new Error('Your request was created, but its chat did not open. Refresh Support to find it.');
  }
  return { ticketId: row.ticket_id, conversationId: row.conversation_id, ticketNumber: row.ticket_number ?? null };
}

/** Materializes the thread for a ticket that has none yet. */
export async function ensureConversation(ticketId: string): Promise<string> {
  const { data, error } = await supabase.rpc('support_get_or_create_conversation', { p_ticket_id: ticketId });
  if (error) throw error;
  if (!data) throw new Error('Could not open the chat for this request');
  return data as string;
}

export async function sendSupportMessage(conversationId: string, body: string, attachments: SupportAttachment[] = []): Promise<string> {
  const { data, error } = await supabase.rpc('support_post_message', {
    p_conversation_id: conversationId,
    p_body: body.trim(),
    p_is_internal: false,
    p_attachments: attachments,
  });
  if (error) throw error;
  return data as string;
}

/** The customer's answer to BMC Assistant. Returns false when the ticket had
 *  already moved on (a human took over, etc.) — callers just refresh. */
export async function aiCustomerAction(ticketId: string, action: 'solved' | 'human'): Promise<boolean> {
  const { data, error } = await supabase.rpc('support_ai_customer_action', { p_ticket_id: ticketId, p_action: action });
  if (error) throw error;
  return data === true;
}

export async function editSupportMessage(messageId: string, body: string): Promise<void> {
  const { error } = await supabase.rpc('support_edit_message', { p_message_id: messageId, p_body: body.trim() });
  if (error) throw error;
}

/** Per-viewer read cursor. RLS pins the row to auth.uid(). A failed receipt
 *  is invisible and the next open retries it, so errors are swallowed. */
export async function markConversationRead(conversationId: string, userId: string): Promise<void> {
  await supabase
    .from('support_conversation_reads')
    .upsert(
      { conversation_id: conversationId, user_id: userId, last_read_at: new Date().toISOString() },
      { onConflict: 'conversation_id,user_id' },
    );
}

// ── Realtime ────────────────────────────────────────────────────────────────

export function subscribeToThread(conversationId: string, onChange: () => void) {
  const channel = supabase
    .channel(`coach_support_thread:${conversationId}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` }, () => onChange())
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversationId}` }, () => onChange())
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

/** Any thread change for this user. Includes their mobile-app threads (the
 *  conversation row doesn't carry is_coach), which only costs a refetch. */
export function subscribeToMyConversations(userId: string, onChange: () => void) {
  const channel = supabase
    .channel(`coach_support_convs:${userId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations', filter: `requester_user_id=eq.${userId}` }, () => onChange())
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}

// Whether the BMC Assistant is switched on (SUPPORT_AI_ENABLED in the
// Brand-Dashboard worker). Only gates the "is typing" row — if this can't be
// read the row simply stays hidden, which is the safe side: a missing
// indicator never misleads, a stuck one does. Successful answers are cached
// for the page's lifetime.
let aiEnabledCache: boolean | null = null;
let aiEnabledInflight: Promise<boolean> | null = null;
export function fetchSupportAiEnabled(): Promise<boolean> {
  if (aiEnabledCache !== null) return Promise.resolve(aiEnabledCache);
  if (!BRAND_WORKER_URL) return Promise.resolve(false);
  if (!aiEnabledInflight) {
    aiEnabledInflight = fetch(`${BRAND_WORKER_URL}/api/support/ai-status`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { enabled?: unknown };
        aiEnabledCache = body.enabled === true;
        return aiEnabledCache;
      })
      .catch(() => false)
      .finally(() => { aiEnabledInflight = null; });
  }
  return aiEnabledInflight;
}
